// Barcode decoding with ZXing (WebAssembly). Used by camera scanning on the web and in
// Electron. The .wasm must be served by the app itself (stations work offline), so the app
// calls `configureZxing({ wasmUrl })` once at start-up; tests pass the bytes instead.

import { parseScan } from '@enp/domain';
import { prepareZXingModule, readBarcodes, type ReaderOptions } from 'zxing-wasm/reader';
import type { ScanRead } from './types.js';

/** What the stations read: lot QR labels, retail barcodes, and common case/pallet codes. */
export const SCAN_FORMATS = [
  'QRCode',
  'DataMatrix',
  'EAN13',
  'EAN8',
  'UPCA',
  'UPCE',
  'Code128',
  'ITF14',
] as const satisfies ReaderOptions['formats'];

export interface ZxingConfig {
  /** URL of `zxing_reader.wasm` served by the app. */
  wasmUrl?: string;
  /** The .wasm bytes (Node tests). */
  wasmBinary?: ArrayBuffer;
}

let configured = false;

/** Points ZXing at a local copy of its .wasm (never the default CDN). Call once. */
export function configureZxing({ wasmUrl, wasmBinary }: ZxingConfig): void {
  prepareZXingModule({
    overrides: {
      ...(wasmBinary ? { wasmBinary } : {}),
      ...(wasmUrl
        ? {
            locateFile: (path: string, prefix: string) =>
              path.endsWith('.wasm') ? wasmUrl : prefix + path,
          }
        : {}),
    },
    fireImmediately: false,
  });
  configured = true;
}

export function isZxingConfigured(): boolean {
  return configured;
}

const READER_OPTIONS: ReaderOptions = {
  formats: [...SCAN_FORMATS],
  tryHarder: true,
  tryInvert: false,
  maxNumberOfSymbols: 1,
};

/**
 * Decodes an image (encoded PNG/JPEG bytes or raw ImageData). Returns the valid codes found,
 * already classified by `parseScan`.
 */
export async function decodeImage(
  input: Blob | ArrayBuffer | Uint8Array | ImageData,
  source: ScanRead['source'] = 'camera',
): Promise<ScanRead[]> {
  const results = await readBarcodes(input, READER_OPTIONS);
  return results
    .filter((r) => r.isValid && r.text !== '')
    .map((r) => ({ text: r.text, format: r.format, parsed: parseScan(r.text), source }));
}
